import Cookies from 'js-cookie';
import type { IArticleConfig } from '~/config/article';
import { ENV } from '~/config/article/env';
import widgetsConfig from '~/config/widgets.config';
import { createMarker, debugLog, markers, measures, measureTimeBetweenMarks, vdebugLog } from '~/debug';
import CommunicationCreator from '~/modules/CommunicationCreator';
import ServerLogger, { ILoggerAction } from '~/modules/ServerLogger';
import Session from '~/modules/Session';
import WidgetsCreator from '~/modules/WidgetsCreator';
import { isInCrossOriginFrame, isInIframe, loadJS, waitForElement } from '~/utils/dom';
import { getFormattedTime } from '~/utils/general';
import { safeLocalStorageGet, safeLocalStorageSetDelete } from '~/utils/localStorage';
import { advancedKeys } from '~/config/keys';
import type AdblockDetector from './AdblockDetector';
import AdUnitCreator from './AdUnitCreator';
import { containsKeyword, getTheGPTNumberOfTheAdTarget, matchesPath } from './AdUnitUtils';
// import QuantcastTag from './QuantcastTag';
import { type IAdvancedConfig } from './types';
import WidgetsConfig from '~/config/widgets.config';
import PrebidAdvertisementUnit from './PrebidAdvertisementUnit';
import { makeRequest } from '@vuukle/utils/dist/es';
import EndlessMode from '~/modules/EndlessMode';
import domainConfigOverrides from '~/config/domainConfigOverrides';

const VUUKLE_GAM_NETWORK_ID = 213794966;

// import { Pubx } from './Pubx';

interface IStickyAd {
  /** The div id for the sticky ad (we will create a div with this id) */
  divId?: string;
  /** The div id for the sticky ad (we will target an existing div with this id) */
  targetDivId?: string;
  /**
   * Place the sticky ad at the left corner, top of the screen or at the center of the screen.
   * Only works with stickyAd option
   *  @default 'center'
   */
  stickyAdPosition?: 'left' | 'center' | 'top';
  /**
   * Place the sticky ad at the bottom or at the top of the screen.
   * Only works with stickyAd option
   * @default 'bottom'
   */
  stickyAdMobilePosition?: 'top' | 'bottom';
  /**
   * Show sticky ad after scrolling down some 'x' number of px
   */
  showStickyAfter?: string;
  /** Hide sticky ad on mobile or desktop */
  hideStickyAdOn?: 'mobile' | 'desktop';
  /** Hide the sticky ad when scrolling, show the sticky ad when not scrolling */
  hideOnScroll?: boolean;
  /** Which countries should have the ad */
  countryInclude?: string[];
}

interface IAdJSONConfig {
  /** The ads on the website are not GPT ads */
  noGpt?: boolean;
  /** Ad video player configuration */
  videoPlayer?: {
    /** Aniview identifier */
    aniviewID: string;
    /** Restrict aniview initialization to desktop */
    onlyDesktop?: boolean;
    /** List of country codes for which the aniview ads will not run */
    countryExclude?: string[];
  };
  /** Type of ad unit used for the ads */
  adUnit?: string;
  /** Google IMA Audio ad ID for the publisher, used for the 'iu' query param of IMA ad tag url. If given, will enable audio ads */
  audioAdID?: string;
  /** Enable/Disable realtime commenting for the publisher */
  realtime?: boolean;
  /** Prevent Traffic Exchange ads from generating */
  noAdsTX?: boolean;
  /** Excluded ad unit divs per country */
  excludedAds?: {
    [key: string]: string[];
  };
  /** Hide sticky ad on mobile or desktop */
  hideStickyAdOn?: 'mobile' | 'desktop';
  stickyAd?: IStickyAd;
  extraStickyAds?: IStickyAd[];
  /** Ad slot settings in Emotes widget */
  emotesAd?: {
    /** Which emote the ad should replace */
    emotesSlot: 1 | 2 | 3 | 4 | 5 | 6;
    /** Which countries should have the ad */
    countryInclude?: string[];
    /** The image url of the ad */
    imageUrl: string;
    /** The url where the ad redirects */
    clickUrl: string;
    /** The label/text for the ad */
    label: string;
  };
  /** Frequency cap of INTL ads in hours */
  INTLFreqCap?: number;
  /** Disable ad refreshing logic */
  disableAdRefresh?: boolean;
  /** Audience segments object for the first party data pixel tags @see IAdvancedConfig.fpd */
  audience_segments?: {
    topic: string;
    segment_id: string;
  }[];
}

const adblockMethodKeys = ['1feb6d34-f2ca-4219-bff0-28be03a6d8da'];

class AdvertisingCreator {
  private adblockDetector: AdblockDetector;
  private adUnitCreator: AdUnitCreator;
  public static canRefreshAds: boolean = true;
  public static canCreateAds: boolean = true;

  constructor(
    index: string,
    private readonly articleConfig: IArticleConfig,
    private communicationsCreator?: CommunicationCreator,
    private widgetsCreator?: WidgetsCreator
  ) {
    createMarker(markers.adStart);
    measureTimeBetweenMarks(measures.platformStartToAdStart, markers.platformStart, markers.adStart);
    /**
     * Don't load ADS:
     * 1. if API key is missing in article config
     * 2. if `vuuklead` value is false in query string
     * 3. The user agent is a bot
     */
    if (!articleConfig.apiKey || Session.isBot) {
      return;
    } else {
      const searchParams = new URLSearchParams(window.location.search);
      /** 2. if `vuuklead` value is false in query string 🔽 */
      if (searchParams.get('vuuklead') === 'false') {
        debugLog(
          'log',
          '%c [VUUKLE] ADs initialization blocked because of debugging key',
          'background: #f66; color: #fff; padding: 5px;'
        );
        return;
      }
    }

    /** Process the advanced configuration normally or fetch it first */
    if (advancedKeys[articleConfig.apiKey]) {
      if (advancedKeys[articleConfig.apiKey].externalConfig) {
        this.fetchAdvancedConfig(index);
      } else {
        this.processAdvancedConfig(advancedKeys[articleConfig.apiKey], index);
      }
    } else {
      /**
       * In case the apiKey is not present, assume we have an external configuration file for the current host
       * The worst case scenario is a 404 error if the external config file doesn't exist
       */
      advancedKeys[articleConfig.apiKey] = {
        externalConfig: true,
      };
      this.fetchAdvancedConfig(index);
    }
  }

  /**
   * Fetch advanced config
   * @param index
   */
  protected fetchAdvancedConfig(index: string) {
    const mergeConfiguration = () => {
      advancedKeys[this.articleConfig.apiKey] = {
        ...advancedKeys[this.articleConfig.apiKey],
        ...window.vuukleAdConfiguration,
      };
    };

    if (window.vuukleAdConfiguration) {
      mergeConfiguration();
      scheduler.postTask(() => {
        this.processAdvancedConfig(window.vuukleAdConfiguration!, index);
      });
      return;
    }

    loadJS(
      `https://cdn.vuukle.com/domain-configs/${
        domainConfigOverrides[this.articleConfig.host] || this.articleConfig.host
      }.js`
    )
      .then(() => {
        if (window.vuukleAdConfiguration) {
          mergeConfiguration();
          scheduler.postTask(() => {
            this.processAdvancedConfig(advancedKeys[this.articleConfig.apiKey], index);
          });
        } else {
          debugLog('error', 'Ad configuration for this host is missing');
        }
      })
      .catch(() => {
        const apiKeyConfig = advancedKeys[this.articleConfig.apiKey];
        if (
          apiKeyConfig &&
          ((Object.keys(apiKeyConfig).length >= 2 && apiKeyConfig.externalConfig) ||
            (Object.keys(apiKeyConfig).length >= 1 && !apiKeyConfig.externalConfig))
        ) {
          scheduler.postTask(() => {
            this.processAdvancedConfig(apiKeyConfig, index);
          });
        }
        scheduler.postTask(
          () => {
            /** The user is likely using an adblocker at this point */
            if (adblockMethodKeys.includes(this.articleConfig.apiKey)) {
              this.adblockDetector?.onAdblockDetected();
            }
            if (this.communicationsCreator) {
              this.communicationsCreator.checkForAdblockers();
            }
            ServerLogger.log('view_page', this.articleConfig);
          },
          { priority: 'background' }
        );
      });
  }

  /**
   * Get the place where we should insert the element. Usually used for endless mode
   * @param selector
   * @param target
   * @param windowContext
   * @returns
   */
  private getLocation = (selector: string, target: string, windowContext: Window = window): HTMLElement | null => {
    const match = windowContext.document.querySelectorAll<HTMLElement>(selector)[AdUnitCreator.articleCounter - 1];
    // When in endless mode, we want to select the nth match of the selector,
    // if we're generating elements for 3rd article we should pick the selector match at index of 2
    if (!match && AdUnitCreator.articleCounter - 1 !== 0) {
      /**
       * There can be situations where for example out of 5 articles, we can have less than 5 matches on querySelectorAll
       * With this loop we're checking previous querySelectorAll matches and selecting them
       * while making sure that there was no element generated already
       */
      const allElements = windowContext.document.querySelectorAll<HTMLElement>(selector);
      for (let i = AdUnitCreator.articleCounter - 1; i >= 0; i--) {
        const element = allElements[i];
        if (element && !element.parentElement?.querySelector(`div[id*=${target}]`)) {
          return element;
        }
      }
    }

    if (match && !match.parentElement?.querySelector(`div[id*=${target}]`)) {
      return match;
    }

    return null;
  };

  private insertContainerDiv = (
    div: HTMLDivElement,
    insert: 'inside' | 'prependInside' | 'before' | 'after',
    selector: string | ((index: string) => HTMLElement | null) | string[],
    elementId: string,
    index: string
  ) => {
    let containerDivLocation: HTMLElement | null = null;

    if (typeof selector === 'string') {
      containerDivLocation = this.articleConfig.endlessMode
        ? this.getLocation(selector, elementId)
        : document.querySelector(selector);
    } else if (typeof selector === 'function') {
      containerDivLocation = selector(index);
    } else {
      for (const selectorItem of selector) {
        const possibleLocation = document.querySelector<HTMLElement>(selectorItem);
        if (possibleLocation) {
          containerDivLocation = possibleLocation;
          break;
        }
      }
    }

    if (containerDivLocation) {
      switch (insert) {
        case 'after':
          containerDivLocation.insertAdjacentElement('afterend', div);
          break;
        case 'before':
          containerDivLocation.insertAdjacentElement('beforebegin', div);
          break;
        case 'prependInside':
          containerDivLocation.insertAdjacentElement('afterbegin', div);
          break;
        default:
          containerDivLocation.appendChild(div);
      }
    }
  };

  /**
   * Apply configurations to Quiz
   * @param {IAdvancedConfig} config Advanced ad configuration
   * @param runPostQuizCallback
   * @param index
   */
  private applyQuizConfig(config: IAdvancedConfig, runPostQuizCallback = true, index: string) {
    this.widgetsCreator?.communicationCreator.applyQuizConfig(config, runPostQuizCallback, index);
  }

  private configureVuukleSite(config: IAdvancedConfig) {
    const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;

    if (typeof config.setVuukleSite === 'string') {
      windowContext.vuukleSite = config.setVuukleSite;
      window.vuukleSite = config.setVuukleSite;
    }
    if (typeof config.setVuukleSite === 'object') {
      const domains = Object.keys(config.setVuukleSite);
      const currentTopDomain = this.articleConfig.host.split('.').slice(-2).join('.');
      const topLevelDomains = domains.map((domain) => domain.split('.').slice(-2).join('.'));
      const doesMatchDomain = topLevelDomains.filter((domain) => domain === currentTopDomain);
      const topLevelDomainMap: Record<string, string> = {};
      domains.forEach((domain: string) => {
        if (typeof config.setVuukleSite === 'object') {
          const vuukleSiteValue = config.setVuukleSite[domain];
          const topDomainName = domain.split('.').slice(-2).join('.');
          topLevelDomainMap[topDomainName] = vuukleSiteValue;
        }
      });
      if (doesMatchDomain[0]) {
        windowContext.vuukleSite = topLevelDomainMap[doesMatchDomain[0]];
        window.vuukleSite = topLevelDomainMap[doesMatchDomain[0]];
      }
    }
  }

  /**
   * Inserts widgets dynamically like the ones configured with the widget config or with the separate config for Quiz
   * @param config
   * @param index
   */
  protected insertWidgetsDynamically(config: IAdvancedConfig, index: string) {
    if (!Session.userCountryDetermined) {
      setTimeout(() => this.insertWidgetsDynamically(config, index), 100);
      return;
    }
    /** Insert widgets manually if config is given. Also check for geo inclusion settings */
    if (
      config.widgets &&
      this.widgetsCreator &&
      (!config.widgets.widgetInsertionGeos ||
        (config.widgets?.widgetInsertionGeos && config.widgets.widgetInsertionGeos.includes(Session.userCountry))) &&
      !this.articleConfig.amp
    ) {
      WidgetsCreator.createWidgets(
        config.widgets,
        AdUnitCreator.articleCounter,
        this.articleConfig,
        index,
        this.widgetsCreator
      );
    }

    const ignorePaths =
      Session.isMobileUser && config.insertQuizAndAd?.quizOptions?.ignorePathsMobile
        ? config.insertQuizAndAd?.quizOptions.ignorePathsMobile || []
        : config.insertQuizAndAd?.quizOptions?.ignorePaths || [];
    const isCurrentPathIgnored = matchesPath(ignorePaths as string[]);
    const isAllowedToInsertInUserGeo =
      !config.insertQuizAndAd?.insertionGeos ||
      (config.insertQuizAndAd?.insertionGeos && config.insertQuizAndAd.insertionGeos.includes(Session.userCountry));
    const isExcludedFromCurrentUserGeo =
      config.insertQuizAndAd?.excludedGeos && config.insertQuizAndAd.excludedGeos.includes(Session.userCountry);
    /** If Quiz widget + Ad configuration is present, user geo matches if geos are configured and the current path is not ignored */
    if (
      config.insertQuizAndAd &&
      this.widgetsCreator &&
      isAllowedToInsertInUserGeo &&
      !isExcludedFromCurrentUserGeo &&
      !this.articleConfig.amp &&
      !isCurrentPathIgnored
    ) {
      WidgetsCreator.createWidgets(
        {
          preInsertionOperation: () => {
            if (config.insertQuizAndAd?.quizOptions?.preQuizInsertionCallback) {
              config.insertQuizAndAd.quizOptions.preQuizInsertionCallback(index);
            }

            const div = document.createElement('div');
            div.style.display = 'flex';
            div.style.flexWrap = 'wrap';
            div.style.backgroundColor = 'transparent';
            div.style.justifyContent = 'center';
            div.style.alignItems = 'center';
            if (!Session.isMobileUser) {
              div.style.gap = '10px';
            }
            div.id = 'vuukle-quiz-container'.concat(index);

            const adTargetsAreDefined = Boolean(config.targets);
            const adDivIsDefined = config.targets && Object.keys(config.targets).some((a) => a.includes('vuukle-ad'));

            if (adTargetsAreDefined && adDivIsDefined) {
              const adSpaceStylesheet = document.createElement('style');
              adSpaceStylesheet.innerHTML = `
                #${'vuukle-quiz-container'.concat(index)} div[id*="vuukle-ad"] {
                  min-height: 250px;
                  min-width: 300px;
                }
              `;
              document.head.appendChild(adSpaceStylesheet);
            }

            if (config.insertQuizAndAd?.containerDivOptions?.containerDivStyles) {
              div.style.cssText += config.insertQuizAndAd.containerDivOptions.containerDivStyles;
            }

            if (config.insertQuizAndAd?.containerDivOptions?.customInsertionFunction) {
              config.insertQuizAndAd.containerDivOptions.customInsertionFunction(div);
            } else {
              this.insertContainerDiv(
                div,
                config.insertQuizAndAd?.quizOptions?.insert || 'inside',
                Session.isMobileUser && config.insertQuizAndAd?.quizOptions?.mobileSelector
                  ? config.insertQuizAndAd?.quizOptions?.mobileSelector
                  : config.insertQuizAndAd?.quizOptions?.selector || '',
                'vuukle-quiz-container',
                index
              );
            }
          },
          quiz: {
            insert: 'inside',
            selector: '#vuukle-quiz-container'.concat(index),
          },
          postInsertionOperation: () => {
            const quizWidget =
              document.querySelector<HTMLDivElement>(WidgetsConfig.selectors.quiz.concat(index)) ||
              document.querySelector<HTMLDivElement>(WidgetsConfig.selectors.quizAlt.concat(index));

            if (quizWidget) {
              const iframeElement = quizWidget.querySelector<HTMLIFrameElement>('iframe');

              quizWidget.style.marginTop = '16px';
              quizWidget.style.marginRight = '1px';
              if (iframeElement) {
                // iframeElement.style.boxShadow = '0 0 8px rgba(0,0,0,.3)';
                iframeElement.style.borderRadius = '6px';
              }
              if (config.insertQuizAndAd?.quizOptions?.quizStyles) {
                quizWidget.style.cssText += config.insertQuizAndAd.quizOptions.quizStyles;
              }
            }

            this.applyQuizConfig(config, true, index);
          },
        },
        AdUnitCreator.articleCounter,
        this.articleConfig,
        index,
        this.widgetsCreator
      );
    }
  }

  /**
   * Collect the user IDs from global prebid scripts of the page and then pass them to our servers
   * @param config
   * @param index
   */
  // private async mapPrebidIDsToUserID() {
  //   let previousGlobalsWithUserIDs: Record<string, Record<string, any>>[] = [];
  //   const innerPrebidIDMapperFunction = async () => {
  //     if (window._pbjsGlobals) {
  //       const prebidGlobalsWithUserIDs: Record<string, Record<string, any>>[] = [];
  //       for (const prebidGlobal of window._pbjsGlobals) {
  //         if (
  //           window[prebidGlobal as keyof Window] &&
  //           typeof window[prebidGlobal as keyof Window].getUserIds === 'function'
  //         ) {
  //           const userIDs = window[prebidGlobal as keyof Window].getUserIds();

  //           const prebidGlobalUserIDs: Record<string, any> = { [prebidGlobal]: {} };
  //           for (const userID in userIDs) {
  //             prebidGlobalUserIDs[prebidGlobal][userID] = userIDs[userID];
  //           }
  //           prebidGlobalsWithUserIDs.push(prebidGlobalUserIDs);
  //         }
  //       }
  //       const areIDObjectsTheSame =
  //         JSON.stringify(prebidGlobalsWithUserIDs) === JSON.stringify(previousGlobalsWithUserIDs);
  //       previousGlobalsWithUserIDs = [...prebidGlobalsWithUserIDs];
  //       if (!areIDObjectsTheSame) {
  //         const results = await makeRequest(
  //           'POST',
  //           `${ENV.API_URL}/api/v1/UserIdCatalog`,
  //           {
  //             host: this.articleConfig.host,
  //             articleId: this.articleConfig.articleId,
  //             userId: Session.userAPIKey,
  //             apiKey: this.articleConfig.apiKey,
  //             bidders: prebidGlobalsWithUserIDs,
  //           },
  //           'application/json'
  //         );
  //         const userID = results.data.userId;
  //         if (!Session.userAPIKey) {
  //           Session.userAPIKey = userID;
  //         }
  //         if (!safeLocalStorageGet('vuukle_user_id')) {
  //           const consentManager = await import(/* webpackChunkName: "cmg" */ '../ConsentManager');
  //           if (Session.canUseCookies) {
  //             consentManager.default.executeWhenStorageValueCanBeSet('vuukle_user_id', () => {
  //               safeLocalStorageSetDelete('vuukle_user_id', userID);
  //             });
  //           } else {
  //             Session.addStorageUpdateCallback(() => {
  //               consentManager.default.executeWhenStorageValueCanBeSet('vuukle_user_id', () => {
  //                 safeLocalStorageSetDelete('vuukle_user_id', userID);
  //               });
  //             });
  //           }
  //         }
  //       }
  //     }
  //   };

  //   innerPrebidIDMapperFunction();
  //   setInterval(() => {
  //     innerPrebidIDMapperFunction();
  //   }, 30000);
  // }

  /**
   * Track the user exiting the page within the first 5 seconds
   */
  private trackFiveSecondExit() {
    const articleConfig = this.articleConfig;
    /**
     *
     */
    function trackPageClose() {
      if (document.visibilityState === 'hidden') {
        // Use logPost with sendBeacon argument
        ServerLogger.logPost(
          'exit_5s',
          articleConfig,
          true,
          articleConfig.endlessMode
            ? [{ key: 'article_id', value: encodeURI(EndlessMode.articleIDs.get(document.location.href) || '') }]
            : []
        );
      }
    }
    window.addEventListener('visibilitychange', trackPageClose);
    setTimeout(() => window.removeEventListener('visibilitychange', trackPageClose), 5000);
  }

  /**
   * Track ads that are being rendered and report them to the BQ server
   * @param geoExcludes
   */
  private trackRenderedAds(geoExcludes?: string[]) {
    if (geoExcludes && !Session.userCountryDetermined) {
      setTimeout(() => {
        this.trackRenderedAds(geoExcludes);
      }, 100);
      return;
    }

    if (geoExcludes && geoExcludes.length > 0) {
      if (geoExcludes.includes(Session.userCountry)) {
        return;
      }
    }

    const addRenderedAdListener = () => {
      window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
        const slot = event.slot;
        debugLog(
          'log',
          `[Vuukle] Ad slot with id of ${slot.getSlotElementId()} and ad unit path of ${slot.getAdUnitPath()} has finished rendering`
        );
        if (!slot.getAdUnitPath().includes(VUUKLE_GAM_NETWORK_ID)) {
          ServerLogger.log('gam_detect', this.articleConfig, [{ key: 'adDivId', value: slot.getAdUnitPath() }]);
        }
      });
    };
    if (!window.googletag?.pubads) {
      const interval = setInterval(() => {
        if (window.googletag?.pubads) {
          clearInterval(interval);
          addRenderedAdListener();
        }
      }, 200);
    } else {
      addRenderedAdListener();
    }
  }

  /**
   * Configs to apply in AMP mode that are usually restricted to non-AMP version
   * @param config
   */
  private applyConfigsInAMP(config: IAdvancedConfig, index: string) {
    this.applyQuizConfig(config, false, index);

    if (config.customQuizStyles) {
      this.widgetsCreator?.communicationCreator?.addCustomStylesQuiz(config.customQuizStyles);
    }
  }

  protected async processAdvancedConfig(config: IAdvancedConfig, index: string) {
    if (typeof config.gaEvents === 'boolean') {
      CommunicationCreator.sendGAEvents = config.gaEvents;
    } else if (typeof config.insertQuizAndAd === 'object' || this.communicationsCreator?.isQuizOnThePage()) {
      CommunicationCreator.sendGAEvents = true;
    }

    scheduler.postTask(() => {
      this.insertWidgetsDynamically(config, index);
    });

    scheduler.postTask(() => {
      if (config.showEmotesArticlesByDefault && this.communicationsCreator) {
        this.communicationsCreator.showEmotesArticlesByDefault();
      }
    });

    const wrapperForAD: HTMLDivElement | null =
      document.querySelector(widgetsConfig.selectors.comments.concat(index)) ||
      document.querySelector(widgetsConfig.selectors.emotes.concat(index));

    // Check for comments and emotes divs and their config to determine if they exist
    const commentsEnabled: boolean =
      document.querySelector(widgetsConfig.selectors.comments.concat(index)) !== null &&
      this.articleConfig.comments.enabled;
    const emotesEnabled: boolean =
      document.querySelector(widgetsConfig.selectors.emotes.concat(index)) !== null &&
      this.articleConfig.emotes.enabled;

    const commentsOrEmotesEnabled: boolean = commentsEnabled || emotesEnabled;

    let canCreateAds = true;
    let noBlockedKeywords = true;
    let noBlockedTags = true;

    /** Show/Hide audio button in CW */
    scheduler.postTask(() => {
      if (typeof config.showCWAudioButton === 'boolean') {
        this.communicationsCreator?.toggleAudioButton(config.showCWAudioButton);
      }
    });

    if (
      (this.articleConfig.amp && config.fireViewPageInAMP && !ServerLogger.viewPageFired) ||
      (!config.skipPageView && !this.articleConfig.noViewPage && !ServerLogger.viewPageFired)
    ) {
      scheduler.postTask(
        () => {
          ServerLogger.log('view_page', this.articleConfig);
        },
        { priority: 'background' }
      );
    }

    if (config.hideAddVuukle) {
      scheduler.postTask(() => {
        this.widgetsCreator?.communicationCreator.hideAddVuukle();
      });
    }

    if (config.hideVuukleLogoInFooter) {
      scheduler.postTask(() => {
        this.widgetsCreator?.communicationCreator.hideVuukleLogoInFooter();
      });
    }

    if (config.track5s) {
      this.trackFiveSecondExit();
    }

    if (config.trackRenderedAds) {
      this.trackRenderedAds(config.trackRenderedAdsGeoExcludes);
    }

    // In AMP mode we need to call the loadAdConfiguration right away and then stop running the rest of the constructor
    if (this.articleConfig.amp) {
      scheduler.postTask(() => {
        this.applyConfigsInAMP(config, '');
        this.loadAdConfiguration(this.articleConfig, config, null, true, '');
      });
      return;
    }

    scheduler.postTask(async () => {
      if (config.CMP) {
        const consentManager = await import(/* webpackChunkName: "cmg" */ '../ConsentManager');
        consentManager.default.loadCMP(config.CMP);
      }
    });

    // Check blocked words
    if (config.blockKeywords) {
      noBlockedKeywords = !containsKeyword(this.articleConfig.url, config.blockKeywords);
    }
    // Check blocked tags
    if (config.blockAdsByTags && this.articleConfig.tags) {
      noBlockedTags = !containsKeyword(this.articleConfig.tags, config.blockAdsByTags);
      if (!noBlockedTags) {
        scheduler.postTask(
          () => {
            ServerLogger.log('ads_false', this.articleConfig);
          },
          { priority: 'background' }
        );
      }
    }
    canCreateAds = noBlockedTags && noBlockedKeywords;

    // When there are specific page variables that disable ads, we should prevent ads from being created
    if (config.disableAdsOnPageVariable) {
      const isPublirSubscribed = Cookies.get('publir_subscriber');

      if (typeof window.publir_show_ads === 'boolean') {
        canCreateAds = window.publir_show_ads;
      }

      if (isPublirSubscribed === '1') {
        canCreateAds = false;
      }
    }

    const adsDisabledWithBool = typeof this.articleConfig.ads === 'boolean' && !this.articleConfig.ads;
    const adsDisabledWithObject =
      typeof this.articleConfig.ads === 'object' && this.articleConfig.ads.enabled === false;

    if (adsDisabledWithBool || adsDisabledWithObject) {
      canCreateAds = false;
    }

    if (typeof config.ads === 'boolean' && config.ads === false) {
      canCreateAds = false;
    }

    if (!AdvertisingCreator.canCreateAds) {
      canCreateAds = false;
    }

    // Used by prebid script to determine which ad configuration file to load. This value is usually a host name like hostname.com
    if (config.setVuukleSite) {
      this.configureVuukleSite(config);
    }

    if (config.quizCallToAction) {
      scheduler.postTask(() => {
        this.communicationsCreator?.activateCTAInQuiz(config.quizCallToAction);
      });
    }

    if (canCreateAds) {
      /** Create ads using advanced configuration if ads can be created */
      this.loadAdConfiguration(this.articleConfig, config, wrapperForAD, commentsOrEmotesEnabled, index, () => {
        if (config.targets && config.waitForAdJson) {
          AdvertisingCreator.loadPrebid(config.adUnit, config.prebid7);
        }
      });
      this.adUnitCreator = new AdUnitCreator(config, this.articleConfig);
      if (config.targets) {
        // AdvertisingCreator.loadPrebid(config.adUnit).then(() => this.mapPrebidIDsToUserID());
        if (!config.waitForAdJson) {
          AdvertisingCreator.loadPrebid(config.adUnit, config.prebid7);
        }
        if (!config.skipAdblockCheck) {
          const adblockDetector = await import(/* webpackChunkName: "abd" */ './AdblockDetector');
          this.adblockDetector = new adblockDetector.default(this.articleConfig, config, this.communicationsCreator);
          this.adblockDetector.loadAdblockerBait();
        }
      }
      scheduler.postTask(() => {
        // Let CW know if it can create ad spaces inside itself or not
        this.communicationsCreator?.toggleAdSpaceCreation(Boolean(config.targets));
      });

      scheduler.postTask(() => {
        /** If there's a config written for inserting TX and there's no TX in the page */
        const isTXPresent = document.querySelector(widgetsConfig.selectors.trafficExchange.concat(index));
        if (!isTXPresent && config.insertTX) {
          const { insert, selector } = config.insertTX;
          // We don't need to explicitly call the WidgetsCreator function for adding TX here
          // it will automatically work after Ad Generation module is done and WidgetsCreator module is initialized
          this.adUnitCreator.insertTXInsideElement(insert, selector, index);
        }
      });
    } else {
      scheduler.postTask(
        () => {
          /** Report the BQ server if the url contains offensive keywords */
          if (!noBlockedKeywords || !noBlockedTags) {
            ServerLogger.log('policy_bad', this.articleConfig);
          }
        },
        { priority: 'background' }
      );
    }

    // Change the placement of the CW bubble button position
    if (config.CWBubblePosition) {
      this.communicationsCreator?.changeCommentsBubblePosition(config.CWBubblePosition);
    }

    if (typeof config.CWBubblePersistence === 'boolean') {
      this.communicationsCreator?.changeCommentsBubblePersistence(config.CWBubblePersistence);
    }

    if (config.customBQEvent) {
      const element = document.querySelector<HTMLElement>(config.customBQEvent.trigger.elementClickSelector);
      if (element) {
        element.addEventListener('click', () => {
          ServerLogger.log(config.customBQEvent?.eventName as ILoggerAction, this.articleConfig);
        });
      }
    }

    this.applyQuizConfig(config, false, index);

    // Send custom configured font to widgets
    if (
      config.customFont &&
      typeof config.customFont.fontFamily === 'string' &&
      (typeof config.customFont.url === 'string' || typeof config.customFont.externalFontStylesheetLink === 'string')
    ) {
      scheduler.postTask(() => {
        this.widgetsCreator?.communicationCreator?.passCustomFontToWidgets(config.customFont);
      });
    }

    if (config.showEmotesArticlesByDefault) {
      scheduler.postTask(() => {
        this.communicationsCreator?.showEmotesArticlesByDefault();
      });
    }

    if (config.customQuizStyles) {
      this.widgetsCreator?.communicationCreator?.addCustomStylesQuiz(config.customQuizStyles);
    }

    if (config.commentsPanelTriggerElement) {
      this.widgetsCreator?.communicationCreator?.addCommentsPanelTrigger(config.commentsPanelTriggerElement);
    }
  }

  /**
   * Load prebid script
   * @param adUnit
   * @param prebid7
   * @param windowContext
   */
  public static async loadPrebid(adUnit: string = 'prebid3', prebid7: boolean = false, windowContext: Window = window) {
    /** TODO: Duplicated code. Refactor later */
    if (prebid7) {
      if (
        windowContext.document.querySelector(`head script[src="https://cdn.vuukle.com/static/prebid7.js"]`) === null &&
        windowContext.document.querySelector(`head script[src="https://cdn.vuukle.com/static/main.js"]`) === null
      ) {
        createMarker(markers.prebidLoadStart);
        measureTimeBetweenMarks(measures.adStartToPrebidLoadStart, markers.adStart, markers.prebidLoadStart);
        measureTimeBetweenMarks(
          measures.platformStartToPrebidLoadStart,
          markers.platformStart,
          markers.prebidLoadStart
        );
        try {
          await loadJS(`https://cdn.vuukle.com/static/main.js`, {}, windowContext);
          await loadJS(`https://cdn.vuukle.com/static/prebid7.js`, {}, windowContext);
          createMarker(markers.prebidLoadEnd);
          measureTimeBetweenMarks(measures.prebidStartToEnd, markers.prebidLoadStart, markers.prebidLoadEnd);
          measureTimeBetweenMarks(measures.platformStartToPrebidLoad, markers.platformStart, markers.prebidLoadEnd);
          PrebidAdvertisementUnit.prebidLoaded = true;

          vdebugLog('info', true, `Vuukle - ${getFormattedTime()} [Platform.js] Prebid7 and main script loaded`);
          setTimeout(() => {
            PrebidAdvertisementUnit.initialPeriodPassed = true;
          }, 5000);
        } catch (_e) {
          createMarker(markers.prebidLoadError);
          measureTimeBetweenMarks(measures.prebidStartToError, markers.prebidLoadStart, markers.prebidLoadError);
          measureTimeBetweenMarks(measures.platformStartToPrebidError, markers.platformStart, markers.prebidLoadError);
          PrebidAdvertisementUnit.prebidBlocked = true;
          PrebidAdvertisementUnit.prebidLoaded = true;
        }
      }
    } else {
      // If prebid is not present in the document head, load it
      if (
        windowContext.document.querySelector(`head script[src="https://cdn.vuukle.com/static/${adUnit}.js"]`) === null
      ) {
        createMarker(markers.prebidLoadStart);
        measureTimeBetweenMarks(measures.adStartToPrebidLoadStart, markers.adStart, markers.prebidLoadStart);
        measureTimeBetweenMarks(
          measures.platformStartToPrebidLoadStart,
          markers.platformStart,
          markers.prebidLoadStart
        );
        try {
          await loadJS(`https://cdn.vuukle.com/static/${adUnit}.js`, {}, windowContext);
          createMarker(markers.prebidLoadEnd);
          measureTimeBetweenMarks(measures.prebidStartToEnd, markers.prebidLoadStart, markers.prebidLoadEnd);
          measureTimeBetweenMarks(measures.platformStartToPrebidLoad, markers.platformStart, markers.prebidLoadEnd);
          PrebidAdvertisementUnit.prebidLoaded = true;

          vdebugLog('info', true, `Vuukle - ${getFormattedTime()} [Platform.js] Prebid script loaded`);
          setTimeout(() => {
            PrebidAdvertisementUnit.initialPeriodPassed = true;
          }, 5000);
        } catch (_e) {
          createMarker(markers.prebidLoadError);
          measureTimeBetweenMarks(measures.prebidStartToError, markers.prebidLoadStart, markers.prebidLoadError);
          measureTimeBetweenMarks(measures.platformStartToPrebidError, markers.platformStart, markers.prebidLoadError);
          PrebidAdvertisementUnit.prebidBlocked = true;
          PrebidAdvertisementUnit.prebidLoaded = true;
        }
      }
    }
  }

  /**
   * Load and put the ad configuration in a config variable
   * @param articleConfig
   * @param advancedConfig
   * @param wrapperForAD
   * @param commentsOrEmotesEnabled
   * @param index
   * @param callback
   */
  private loadAdConfiguration(
    articleConfig: IArticleConfig,
    advancedConfig: IAdvancedConfig,
    wrapperForAD: HTMLDivElement | null,
    commentsOrEmotesEnabled: boolean,
    index: string,
    callback?: () => void
  ) {
    createMarker(markers.adConfigLoadStart);
    measureTimeBetweenMarks(measures.adStartToConfigLoadStart, markers.adStart, markers.adConfigLoadStart);
    const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;
    /** In case the config is already loaded, proceed without fetching the file */
    if (window._vuukleConfig || windowContext._vuukleConfig) {
      callback?.();
      this.processAdConfiguration(
        window._vuukleConfig || windowContext._vuukleConfig,
        advancedConfig,
        wrapperForAD,
        articleConfig,
        commentsOrEmotesEnabled,
        index
      );
      return;
    }

    const thirtyMinsInMilliseconds = 30 * 60 * 1000;
    // Contains domain json file
    const vuukleConf = safeLocalStorageGet(
      `vuukleconfhttps://cdn.vuukle.com/ads/${
        advancedConfig.setVuukleSite ? windowContext.vuukleSite : articleConfig.host
      }.json`
    );
    // Contains domain json cache time in unix milliseconds
    const vuukleConfTime = safeLocalStorageGet(
      `vuukleconftimehttps://cdn.vuukle.com/ads/${
        advancedConfig.setVuukleSite ? windowContext.vuukleSite : articleConfig.host
      }.json`
    );

    /** Get domain json from localStorage. If the data is newer than 30 minutes old - use it */
    if (vuukleConf && vuukleConfTime) {
      try {
        const confData = JSON.parse(vuukleConf);
        if (new Date().valueOf() - Number(vuukleConfTime) < thirtyMinsInMilliseconds) {
          callback?.();
          this.processAdConfiguration(
            confData.data,
            advancedConfig,
            wrapperForAD,
            articleConfig,
            commentsOrEmotesEnabled,
            index
          );
          return;
        }
      } catch (e) {
        debugLog('error', e);
      }
    }

    const handleNetworkResponse = (response: any) => {
      const saveConfigInBrowserStorage = () => {
        safeLocalStorageSetDelete(
          `vuukleconfhttps://cdn.vuukle.com/ads/${
            advancedConfig.setVuukleSite ? windowContext.vuukleSite : articleConfig.host
          }.json`,
          JSON.stringify({
            time: new Date().valueOf(),
            data: response,
          })
        );
        safeLocalStorageSetDelete(
          `vuukleconftimehttps://cdn.vuukle.com/ads/${
            advancedConfig.setVuukleSite ? windowContext.vuukleSite : articleConfig.host
          }.json`,
          new Date().valueOf().toString()
        );
      };
      window._vuukleConfig = response;
      windowContext._vuukleConfig = response;
      callback?.();
      if (Session.canUseCookies) {
        saveConfigInBrowserStorage();
      } else {
        Session.addStorageUpdateCallback(() => {
          saveConfigInBrowserStorage();
        });
      }
      createMarker(markers.adConfigLoadEnd);
      measureTimeBetweenMarks(measures.adStartToConfigLoadEnd, markers.adConfigLoadStart, markers.adConfigLoadEnd);
      measureTimeBetweenMarks(measures.platformStartToAdConfigLoadEnd, markers.platformStart, markers.adConfigLoadEnd);
      this.processAdConfiguration(
        response,
        advancedConfig,
        wrapperForAD,
        articleConfig,
        commentsOrEmotesEnabled,
        index
      );
    };

    const createAndSendAdsConfigRequest = (link: string) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', link, true);
      xhr.setRequestHeader('Content-Type', 'text/plain');
      xhr.send();
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (response.errors && response.errors[0] === 'no_cache') {
            createAndSendAdsConfigRequest(
              `${ENV.API_URL}/api/v1/AdsJson/fillKVcacheForHost?host=${articleConfig.host}`
            );
          } else {
            handleNetworkResponse(response);
          }
        } else if (xhr.status !== 200) {
          createMarker(markers.adConfigLoadError);
          measureTimeBetweenMarks(measures.adConfigStartToError, markers.adConfigLoadStart, markers.adConfigLoadError);
          measureTimeBetweenMarks(
            measures.platformStartToAdConfigLoadError,
            markers.platformStart,
            markers.adConfigLoadError
          );
        }
      };
      xhr.onerror = () => {
        /** The user is likely using an adblocker at this point */
        if (adblockMethodKeys.includes(articleConfig.apiKey)) {
          this.adblockDetector?.onAdblockDetected();
        }
      };
    };
    // const isDev = ENV.NODE_ENV === 'development' || ENV.IS_STAGING;
    // const hostConfigLink = isDev ? 'https://cdntest.vuukle.com/getAdsConfig' : 'https://cdn.vuukle.com/getAdsConfig';
    // createAndSendAdsConfigRequest(
    //   adsCacheKeys.includes(articleConfig.apiKey)
    //     ? `${hostConfigLink}/?host=${articleConfig.host}`
    //     : `https://cdn.vuukle.com/ads/${articleConfig.host}.json`
    createAndSendAdsConfigRequest(
      `https://cdn.vuukle.com/ads/${advancedConfig.setVuukleSite ? windowContext.vuukleSite : articleConfig.host}.json`
    );
  }

  /**
   * Process configuration from either host.json file or global ads config
   * @param adsConfig
   * @param advancedConfig
   * @param wrapperForAD
   * @param articleConfig
   * @param commentsOrEmotesEnabled
   * @param index
   */
  private async processAdConfiguration(
    adsConfig: IAdJSONConfig,
    advancedConfig: IAdvancedConfig,
    wrapperForAD: HTMLDivElement | null,
    articleConfig: IArticleConfig,
    commentsOrEmotesEnabled: boolean,
    index: string
  ) {
    // Update global ads config if not present
    if (!window._vuukleConfig) {
      window._vuukleConfig = adsConfig;
    }

    // Set if the page ads are GPT or not
    if (typeof adsConfig.noGpt === 'boolean') {
      PrebidAdvertisementUnit.noGPT = adsConfig.noGpt;
    }

    // Realtime will not work for users with an adblocker
    // if (adsConfig.realtime) {
    //   scheduler.postTask(() => {
    //     if (this.realtimeAnalytics) {
    //       this.realtimeAnalytics.enabled = true;
    //       this.realtimeAnalytics.init(articleConfig);
    //     }

    //     if (this.communicationsCreator) {
    //       this.communicationsCreator.enableRealtime();
    //     }
    //   });
    // }

    /** Stop ad generation in AMP mode */
    if (articleConfig.amp) {
      return;
    }

    AdUnitCreator.canCreateTXAds = !adsConfig.noAdsTX;

    const config: IAdvancedConfig = advancedKeys[articleConfig.apiKey];

    /** Disable ad refresh if configured to do so */
    if (adsConfig.disableAdRefresh) {
      AdvertisingCreator.canRefreshAds = false;
    }

    const createAds = () => {
      const someAdsAreGeoRestricted =
        config.targets && Object.values(config.targets).some((val) => val.adUnitExcludedGeos || val.adUnitGeos);

      /** We can not proceed with ad creation for hosts with excludedAds config or geo restricted ads until we know where the user is from */
      if (!Session.userCountryDetermined && adsConfig.excludedAds && someAdsAreGeoRestricted) {
        setTimeout(createAds, 100);
        return;
      }

      if (adsConfig.excludedAds && adsConfig.excludedAds[Session.userCountry]) {
        const excludedTargets = adsConfig.excludedAds[Session.userCountry];
        excludedTargets.forEach((target) => {
          delete config.targets?.[target];
        });
      }

      /** Insert pubx ad optimization script. It must be inserted before prebid.js */
      // if (config.pubxID) {
      //   new Pubx(config.pubxID);
      // }

      scheduler.postTask(
        () => {
          // Learn about the ad unit and initialize ad creation
          this.adUnitCreator.createAdvancedAd(wrapperForAD, commentsOrEmotesEnabled, index);
        },
        { priority: 'user-blocking' }
      );

      scheduler.postTask(() => {
        // Replace other adsense ad units on the page with GPT units
        if (!config.excludeAdSenseRefreshFrom || !config.excludeAdSenseRefreshFrom?.includes(articleConfig.host)) {
          this.adUnitCreator.findAndReplaceAdSenseUnits(index);
        }
        this.adUnitCreator.findAndReplaceExistingAdUnits(index);
      });

      scheduler.postTask(() => {
        if (config.targets) {
          const adsThatAreReplaced: string[] = [];
          const adReplacementMap: Record<string, string> = {};
          const adInViewRefreshPercentages: Record<string, number> = {};
          Object.entries(config.targets).forEach(([targetName, target]) => {
            if (target.replaceAdOnPage && target.replaceAdOnPage.adDivId) {
              adsThatAreReplaced.push(target.replaceAdOnPage.adDivId);
              adsThatAreReplaced.push(targetName);
              adReplacementMap[target.replaceAdOnPage.adDivId] = targetName;
              adReplacementMap[targetName] = targetName;
              adInViewRefreshPercentages[target.replaceAdOnPage.adDivId] =
                target.replaceAdOnPage.inViewRefreshPercentage || 50;
              adInViewRefreshPercentages[targetName] = target.replaceAdOnPage.inViewRefreshPercentage || 50;
            }
          });
          if (adsThatAreReplaced.length > 0) {
            this.adUnitCreator.replaceAdsWhenInView(
              adsThatAreReplaced,
              adReplacementMap,
              adInViewRefreshPercentages,
              index
            );
          }
        }
      });

      scheduler.postTask(() => {
        if (config.customAdRefresh) {
          this.adUnitCreator.performCustomAdRefreshes(config.customAdRefresh, index);
        }
      });

      // scheduler.postTask(() => {
      //   if (config.quizNewsfeedPopupAdId) {
      //     /** We need to use a setInterval here because the Quiz Newsfeed Popup instance may not be ready by the time this executes */
      //     const interval = setInterval(() => {
      //       if (CommunicationCreator.quizNewsfeedPopupInstance && config.quizNewsfeedPopupAdId) {
      //         CommunicationCreator.quizNewsfeedPopupInstance.injectAd(config.quizNewsfeedPopupAdId);
      //         clearInterval(interval);
      //       }
      //     }, 100);
      //   }
      // });

      if (config.iframeIDForPlatformInjection) {
        setTimeout(async () => {
          const iframe = (await waitForElement(
            `iframe[id="${config.iframeIDForPlatformInjection}"]`,
            1000,
            20
          )) as HTMLIFrameElement;
          if (iframe && iframe.contentDocument && iframe.contentWindow) {
            iframe.contentWindow.VUUKLE_CONFIG = this.articleConfig;
            const platformScript = iframe.contentDocument.createElement('script');
            platformScript.src = 'https://cdn.vuukle.com/platform.js';
            iframe.contentDocument.head.appendChild(platformScript);
            iframe.style.minWidth = Session.isMobileUser ? '300px' : '650px';
            iframe.style.minHeight = Session.isMobileUser ? '700px' : '350px';
          }
        }, 0);
      }
    };

    scheduler.postTask(
      () => {
        createAds();
      },
      { priority: 'user-blocking' }
    );

    scheduler.postTask(async () => {
      // Initialize video player
      if (adsConfig.videoPlayer) {
        const isCountryExcluded =
          adsConfig.videoPlayer.countryExclude && adsConfig.videoPlayer.countryExclude.includes(Session.userCountry);
        if (!isCountryExcluded) {
          /** Check if we have device restrictions */
          if (typeof adsConfig.videoPlayer.onlyDesktop === 'boolean') {
            // If desktop-only and user is not on mobile/tablet
            if (adsConfig.videoPlayer.onlyDesktop && !Session.isMobileUser) {
              const aniview = await import(/* webpackChunkName: "anv" */ './Aniview');
              new aniview.default(adsConfig.videoPlayer.aniviewID);
            }

            // If not desktop-only (therefore mobile/tablet only) and user is on mobile/tablet
            if (!adsConfig.videoPlayer.onlyDesktop && Session.isMobileUser) {
              const aniview = await import(/* webpackChunkName: "anv" */ './Aniview');
              new aniview.default(adsConfig.videoPlayer.aniviewID);
            }
          } else {
            // proceed with generating aniview for every device
            const aniview = await import(/* webpackChunkName: "anv" */ './Aniview');
            new aniview.default(adsConfig.videoPlayer.aniviewID);
          }
        }
      }
    });

    scheduler.postTask(() => {
      /** If emotes ad is available */
      if (adsConfig.emotesAd) {
        const countryRestricted = adsConfig.emotesAd.countryInclude && adsConfig.emotesAd.countryInclude.length > 0;
        if (countryRestricted) {
          const isInEligibleCountry = adsConfig.emotesAd.countryInclude?.includes(Session.userCountry);
          if (isInEligibleCountry) {
            // Pass the ad to emotes
            this.communicationsCreator?.sendAdToEmotes(
              adsConfig.emotesAd.emotesSlot,
              adsConfig.emotesAd.imageUrl,
              adsConfig.emotesAd.clickUrl,
              adsConfig.emotesAd.label
            );
          }
        } else {
          // Pass the ad to emotes
          this.communicationsCreator?.sendAdToEmotes(
            adsConfig.emotesAd.emotesSlot,
            adsConfig.emotesAd.imageUrl,
            adsConfig.emotesAd.clickUrl,
            adsConfig.emotesAd.label
          );
        }
      }
    });

    scheduler.postTask(() => {
      /** Enable audio ads in powerbar */
      if (adsConfig.audioAdID) {
        this.communicationsCreator?.enableAudioAds(adsConfig.audioAdID);
      }
    });

    scheduler.postTask(() => {
      /** Process sticky ad configuration and create sticky ads on the page */
      if (adsConfig.stickyAd || adsConfig.extraStickyAds) {
        this.processStickyAdConfigs(adsConfig, config, articleConfig, index);
      }
    });

    scheduler.postTask(async () => {
      // Add sovrn beacon script to the page if configured
      if (advancedConfig.sovrnBeacon) {
        const sovrn = await import(/* webpackChunkName: "svrn" */ './SovrnBeacon');
        new sovrn.default();
      }
    });

    scheduler.postTask(async () => {
      if (advancedConfig.enableGeoEdge) {
        const geoEdge = await import(/* webpackChunkName: "ged" */ './GeoEdge');
        new geoEdge.default();
      }
    });

    scheduler.postTask(async () => {
      /** Initialize and show an INTL ad */
      if (advancedConfig.INTLAd) {
        const ignorePaths =
          Session.isMobileUser && advancedConfig.INTLAd.ignorePathsMobile
            ? advancedConfig.INTLAd.ignorePathsMobile
            : advancedConfig.INTLAd.ignorePaths;
        if (
          !advancedConfig.INTLAd.ignorePaths ||
          !advancedConfig.INTLAd.ignorePathsMobile ||
          (ignorePaths && !matchesPath(ignorePaths))
        ) {
          const gptNumber: string | undefined =
            advancedConfig.targets?.[advancedConfig.INTLAd.adId]?.gptNumber ||
            getTheGPTNumberOfTheAdTarget(advancedConfig.INTLAd.adId);
          const intl = await import(/* webpackChunkName: "intl" */ './INTL');
          new intl.default(
            advancedConfig.INTLAd.adId,
            advancedConfig.INTLAd.desktopOnly,
            advancedConfig.INTLAd.mobileOnly,
            adsConfig.INTLFreqCap || 2,
            gptNumber,
            advancedConfig.targets?.[advancedConfig.INTLAd.adId]?.gptId
          );
          this.adUnitCreator.createAdAtCustomLocation(
            document.querySelector(`#${advancedConfig.INTLAd.adId}`),
            advancedConfig.INTLAd.adId,
            gptNumber,
            advancedConfig.targets?.[advancedConfig.INTLAd.adId]?.gptId,
            index
          );
        }
      }
    });

    scheduler.postTask(async () => {
      /** Initialize in-image ads */
      if (advancedConfig.inImageAd) {
        const inImageAd = await import(/* webpackChunkName: "iia" */ './InImageAd');
        new inImageAd.default(advancedConfig.inImageAd.slotId, advancedConfig.inImageAd.activeGeos);
      }
    });

    /** Initialize usage of pixel tags for audience segments */
    if (advancedConfig.fpd && advancedConfig.network_code) {
      const audienceSegmentsFile = await makeRequest(
        'GET',
        `https://cdn.vuukle.com/ads/quizzly/${this.articleConfig.apiKey}.json`,
        undefined,
        'application/json'
      );
      if (audienceSegmentsFile.audience_segments) {
        this.widgetsCreator?.communicationCreator?.enableAudienceTrackingPixel(
          advancedConfig.network_code,
          audienceSegmentsFile.audience_segments
        );
      }
    }

    /** Initialize quantcast tag */
    // new QuantcastTag();

    createMarker(markers.adEnd);
    measureTimeBetweenMarks(measures.adStartToEnd, markers.adStart, markers.adEnd);
    measureTimeBetweenMarks(measures.platformStartToAdEnd, markers.platformStart, markers.adEnd);
  }

  /**
   * Process sticky ad configurations and create sticky ads
   * @param adsConfig
   * @param config
   * @param articleConfig
   * @param index
   */
  private processStickyAdConfigs(
    adsConfig: IAdJSONConfig,
    config: IAdvancedConfig,
    articleConfig: IArticleConfig,
    index: string
  ) {
    const processIndividualStickyAdConfig = async (stickyAd: IStickyAd) => {
      const countryRestricted = stickyAd.countryInclude && stickyAd.countryInclude.length > 0;
      const isInEligibleCountry = stickyAd.countryInclude?.includes(Session.userCountry);
      if (countryRestricted && !isInEligibleCountry) {
        return;
      }
      // Sticky ad will be centered if it's specified to be centered or if the position is not given
      const isCenter =
        stickyAd.stickyAdPosition === 'center' || stickyAd.stickyAdPosition === 'top' || !stickyAd.stickyAdPosition;
      let adDiv: HTMLDivElement | null = null;
      /** Use either a div id that we will use to insert the ad ourselves or target an existing div on the page for the sticky ad */
      if (stickyAd.divId) {
        adDiv = document.createElement('div');
        adDiv.id = stickyAd.divId;
        document.body.appendChild(adDiv);
      } else if (stickyAd.targetDivId) {
        adDiv = document.getElementById(stickyAd.targetDivId) as HTMLDivElement;
      }
      // supply the gpt id number based on the number on target (vuukle-ad-2, vuukle-ad-3, etc)
      const adUnitName = stickyAd.targetDivId || stickyAd.divId || '';
      const gptNumber: string | undefined =
        config.targets?.[adUnitName]?.gptNumber || getTheGPTNumberOfTheAdTarget(adUnitName);
      this.adUnitCreator.createAdAtCustomLocation(
        adDiv,
        adUnitName,
        gptNumber,
        config.targets?.[adUnitName]?.gptId,
        index
      );
      if (adDiv) {
        const hideSticky = adsConfig.hideStickyAdOn || (stickyAd && stickyAd.hideStickyAdOn);
        const stickyAdModule = await import(/* webpackChunkName: "stk" */ './StickyAd');
        new stickyAdModule.default(
          articleConfig,
          adDiv,
          adUnitName,
          {
            height: isCenter ? 90 : 250,
            mobilePosition: stickyAd.stickyAdMobilePosition,
            position: stickyAd.stickyAdPosition,
            width: isCenter ? 728 : 300,
            showAfter: stickyAd.showStickyAfter,
            hideOnScroll: stickyAd.hideOnScroll,
          },
          hideSticky
        );
      }
    };

    if (adsConfig.stickyAd) {
      processIndividualStickyAdConfig(adsConfig.stickyAd);
    }

    if (adsConfig.extraStickyAds) {
      adsConfig.extraStickyAds.forEach((stickyAd) => {
        processIndividualStickyAdConfig(stickyAd);
      });
    }
  }
}

export default AdvertisingCreator;
